import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { CompanyApi } from "@/types/api/companies";

/**
 * Company type used in the Settings-Company page UI
 */
export interface CompanySettingsUI {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'pending_setup';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  lastSyncedAt?: string;
  // UI convenience aliases for integration linking
  linkedEvoSubscriptionId?: string;
  linkedSubscriptionName?: string;
  linkedProductName?: string;
  linkedSubLedger?: string;
  // Raw backend fields
  code?: string;
  evoSubscriptionId?: string;
  evoOrganisationId?: string;
  linkedSubscriptionId?: string;
  linkedProductId?: string;
  externalCompanyCode?: string;
  externalCompanyName?: string;
  isMultiCompany?: boolean;
  currencyCode?: string;
  taxId?: string;
  address?: string;
  vatRegistrationNumber?: string;
  companyRegistrationNumber?: string;
  phone?: string;
  email?: string;
}

/**
 * Mock companies data for demo mode
 */
const mockCompanies: CompanySettingsUI[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    status: 'active',
    logo: undefined,
    description: 'Leading technology solutions provider',
    integrationStatus: 'connected',
    userCount: 24,
    createdAt: '2024-01-15T10:00:00Z',
    lastSyncedAt: '2025-06-10T08:45:00Z',
    code: 'ACME001',
    currencyCode: 'GBP',
    linkedSubscriptionName: 'Demo Instance 1',
    linkedProductName: 'Access Financials',
    linkedSubLedger: 'SL1',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    status: 'active',
    logo: undefined,
    description: 'Manufacturing and distribution',
    integrationStatus: 'connected',
    userCount: 18,
    createdAt: '2024-02-20T14:30:00Z',
    lastSyncedAt: '2025-06-09T16:20:00Z',
    code: 'GLOB002',
    currencyCode: 'EUR',
    linkedSubscriptionName: 'Demo Instance 2',
    linkedProductName: 'Access Dimensions',
    linkedSubLedger: 'SL3',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    status: 'inactive',
    logo: undefined,
    description: 'Software development company',
    integrationStatus: 'none',
    userCount: 12,
    createdAt: '2024-03-10T09:15:00Z',
    lastSyncedAt: undefined,
    code: 'TECH003',
    currencyCode: 'USD',
    linkedSubscriptionName: undefined,
    linkedProductName: undefined,
    linkedSubLedger: undefined,
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    status: 'active',
    logo: undefined,
    description: 'Retail management systems',
    integrationStatus: 'connected',
    userCount: 31,
    createdAt: '2024-01-05T11:45:00Z',
    lastSyncedAt: '2025-06-11T11:05:00Z',
    code: 'RETAIL4',
    currencyCode: 'GBP',
    linkedSubscriptionName: 'Demo Instance 1',
    linkedProductName: 'Access Financials',
    linkedSubLedger: 'SL2',
  },
];

/**
 * Map backend status string to UI status type.
 * The backend can return 'pending_setup' for auto-generated sub-ledger companies.
 */
function mapCompanyStatus(status: string): 'active' | 'inactive' | 'pending_setup' {
  if (status === 'active') return 'active';
  if (status === 'pending_setup') return 'pending_setup';
  return 'inactive';
}

/**
 * Derive integration status from backend fields.
 * A company is 'connected' if it has a linked subscription — externalCompanyCode is not required
 * (it only represents sub-ledger mapping, not the integration link itself).
 * Auto-generated companies are distinguished by their 'pending_setup' status, not by integration status.
 */
function deriveIntegrationStatus(company: CompanyApi): 'connected' | 'partial' | 'none' {
  if (company.linkedSubscriptionId) return 'connected';
  return 'none';
}

/**
 * Map API response to UI format
 */
function mapCompanyApiToUi(company: CompanyApi): CompanySettingsUI {
  return {
    id: String(company.id),
    name: company.name,
    status: mapCompanyStatus(company.status),
    logo: undefined,
    description: undefined,
    // Integration status considers both linked subscription and whether the company has a sub-ledger assigned.
    // Auto-generated companies have linkedSubscriptionId (via their group) but no externalCompanyCode yet,
    // so they should show as pending rather than connected.
    integrationStatus: deriveIntegrationStatus(company),
    userCount: 0,
    createdAt: company.createdAt,
    lastSyncedAt: company.lastSyncedAt ?? undefined,
    // UI convenience aliases for integration linking
    linkedEvoSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedSubscriptionName: company.linkedSubscriptionName ?? undefined,
    linkedProductName: company.linkedProductName ?? undefined,
    linkedSubLedger: company.externalCompanyCode ?? undefined,
    // Raw backend fields
    code: company.code ?? undefined,
    evoSubscriptionId: company.evoSubscriptionId ?? undefined,
    evoOrganisationId: company.evoOrganisationId ?? undefined,
    linkedSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedProductId: company.linkedProductId ?? undefined,
    externalCompanyCode: company.externalCompanyCode ?? undefined,
    externalCompanyName: company.externalCompanyName ?? undefined,
    isMultiCompany: company.isMultiCompany,
    currencyCode: company.currencyCode ?? undefined,
    taxId: company.taxId ?? undefined,
    address: undefined,
    vatRegistrationNumber: undefined,
    companyRegistrationNumber: undefined,
    phone: undefined,
    email: undefined,
  };
}

/**
 * Hook to fetch companies list data for the Settings-Company page
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useCompaniesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CompanySettingsUI[], Error>(
    ["settings-companies"],
    async ({ signal }) => {
      const response = await apiGet<CompanyApi[]>("/api/v1/companies", undefined, signal);
      return response.map(mapCompanyApiToUi);
    },
    mockCompanies,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}
